import React, { useState, useMemo, useEffect } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import DataGridTable from '@components/DataGridTable/DataGridTable';
import PageHeader from '@components/common/PageHeader';
import Input from '@/components/Form/Input/Input';
import Icon from '@components/common/Icon';
import { TableRowActionsTooltipProvider, TableRowIconActions } from '@components/common/TableRowIconActions';
import { adminStudentsService } from '@services/admin-students/adminStudentsService';

const AdminStudentsList: React.FC = () => {
    const { t } = useTranslation('students');
    const navigate = useNavigate();
    
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    const [pageIndex, setPageIndex] = useState(0);
    const PAGE_SIZE = 20;

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const listQueryKey = ['admin-students', pageIndex, PAGE_SIZE, debouncedSearch];

    const { data: studentsResponse, isLoading, isError } = useQuery({
        queryKey: listQueryKey,
        queryFn: () => adminStudentsService.getAll({ search: debouncedSearch, page: pageIndex + 1, limit: PAGE_SIZE }),
        placeholderData: previousData => previousData,
        staleTime: 5 * 60 * 1000,
    });

    const students = useMemo(
        () => studentsResponse?.data?.data ?? [],
        [studentsResponse]
    );

    const columns = useMemo<ColumnDef<any>[]>(() => {
        return [
            {
                accessorKey: 'student',
                header: t('admin.directory.table.student'),
                size: 250,
                cell: ({ row }) => (
                    <div className="flex flex-col">
                        <span className="font-semibold text-[var(--secondary-color)]">
                            {row.original.first_name} {row.original.last_name}
                        </span>
                        <span className="text-xs text-[var(--disabled-text-color)] mt-0.5">
                            {row.original.email}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: 'enrollment_number',
                header: t('admin.directory.table.enrollmentId'),
                size: 150,
                cell: ({ row }) => (
                    <span className="font-mono text-sm text-[var(--secondary-color)] bg-gray-50 px-2 py-1 rounded border border-gray-100">
                        {row.original.enrollment?.enrollment_number || 'N/A'}
                    </span>
                ),
            },
            {
                accessorKey: 'cohort',
                header: t('admin.directory.table.cohort'),
                size: 200,
                cell: ({ row }) => (
                    <span className="text-sm text-[var(--secondary-color)]">
                        {row.original.enrollment?.cohort?.generated_cohort_title || t('admin.directory.table.unassigned')}
                    </span>
                ),
            },
            {
                accessorKey: 'status',
                header: t('admin.directory.table.status'),
                size: 120,
                cell: ({ row }) => {
                    const isActive = row.original.is_active;
                    return (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold
                            ${isActive ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                            {isActive ? t('admin.directory.table.active') : t('admin.directory.table.inactive')}
                        </span>
                    );
                },
            },
            {
                id: 'actions',
                header: t('admin.directory.table.actions'),
                size: 120,
                enableSorting: false,
                cell: ({ row }) => {
                    const student = row.original;
                    const actions = [
                        {
                            icon: 'eyeShowPrimary' as const,
                            label: t('admin.details.title') || 'View Details',
                            onClick: () => navigate(`/admin/students/${student.id}`),
                        }
                    ];
                    return <TableRowIconActions actions={actions} />;
                },
            },
        ];
    }, [t, navigate]);

    const searchIcon = (
        <button className="flex items-center cursor-pointer">
            <Icon name='search' className='w-3 2xl:w-[14px] h-auto' />
        </button>
    );

    return (
        <TableRowActionsTooltipProvider>
            <>
                <PageHeader
                    title={t('admin.directory.title')}
                    breadcrumbItems={[
                        { label: t('admin.directory.title'), to: '/admin/students' }
                    ]}
                />
                
                <div className="flex flex-col gap-4 2xl:gap-5 !pt-0 p-3 md:p-4 2xl:p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4 2xl:gap-5 p-3 2xl:p-5 pb-[2px] 2xl:pb-[5px] bg-white rounded-2xl border border-[var(--neutral-color)]">
                        <div className="lg:col-start-4">
                            <Input
                                type="text"
                                placeholder={t('admin.directory.searchPlaceholder')}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                leftElement={searchIcon}
                            />
                        </div>
                    </div>

                    {isError ? (
                        <div className="bg-white rounded-2xl border border-[var(--neutral-color)] p-4 text-sm text-[var(--semantic-error-color)]">
                            Failed to load students
                        </div>
                    ) : (
                        <DataGridTable
                            columns={columns}
                            data={students}
                            loading={isLoading}
                            totalCount={studentsResponse?.data?.meta?.total || 0}
                            pageIndex={pageIndex}
                            pageSize={PAGE_SIZE}
                            onPageChange={setPageIndex}
                        />
                    )}
                </div>
            </>
        </TableRowActionsTooltipProvider>
    );
};

export default AdminStudentsList;
